import { NumberInput, Select, Textarea, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Controller } from 'react-hook-form';
import type { FieldDescriptor, FieldRegistry, LayoutDocument } from './types';
import type { LeaseTractInputSchema } from '../schemas';

/**
 * Lease-tract edit-form field registry. Mirrors
 * api/src/lib/lease-tract-form-layout-schema.ts — the field-name list below
 * MUST stay in sync with that file's `LEASE_TRACT_FORM_FIELD_NAMES` (23
 * fields), and both EXCLUDE `lease_id` (it's context, set once from the
 * owning lease at create time, never a layout-editable / PATCH-able field).
 *
 * Mechanical clone of well-fields.tsx / lease-fields.tsx: same context-bag
 * shape (members / canAssignAnyReviewer / currentUserId — no `lookups` here,
 * unlike wells/leases, since lease-tracts has no lookup-backed selects: type
 * and status are plain free-text columns per spec §2.1) and the same
 * reviewer field behavior (server-side change-only authz lives in
 * api/src/routes/lease-tracts.ts's assertReviewerAssignable — mirrored, not
 * reimplemented).
 */

export const LEASE_TRACT_FORM_FIELD_NAMES = [
  'agreement_number',
  'type',
  'status',
  'reviewer_user_id',

  'lessor',
  'lessee',

  'agreement_date',
  'expiration_date',

  'book',
  'page',
  'county',
  'state',

  'legal_description',
  'legal_remark',
  'all_depths',

  'gross_acres',
  'net_acres',

  'royalty',
  'orri_owner',
  'orri_decimal',
  'wi_owner',
  'wi_decimal',
  'other_burdens',
] as const;

export type LeaseTractFormFieldName = (typeof LEASE_TRACT_FORM_FIELD_NAMES)[number];

export interface LeaseTractFormMemberOption {
  value: string;
  label: string;
}

export interface LeaseTractFormContext {
  /** Current project's members, for the reviewer_user_id dropdown. */
  members: LeaseTractFormMemberOption[];
  /** owner/superadmin may assign any member; moderator/editor are self-only. */
  canAssignAnyReviewer: boolean;
  currentUserId: string;
}

type LeaseTractFieldDescriptor = FieldDescriptor<LeaseTractInputSchema, LeaseTractFormContext>;

function textProps(
  descriptor: { label: string; placeholder?: string },
  disabled: boolean,
  required = false,
) {
  const props: { label: string; placeholder?: string; disabled?: boolean; required?: boolean } = {
    label: descriptor.label,
  };
  if (descriptor.placeholder !== undefined) props.placeholder = descriptor.placeholder;
  if (disabled) props.disabled = true;
  if (required) props.required = true;
  return props;
}

function textField(
  label: string,
  defaultSpan: number,
  opts: { placeholder?: string; required?: boolean } = {},
): LeaseTractFieldDescriptor {
  const desc: LeaseTractFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <TextInput {...textProps(descriptor, disabled, opts.required)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function textareaField(label: string, defaultSpan: number): LeaseTractFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <Textarea autosize minRows={2} {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
}

function dateField(label: string, defaultSpan: number): LeaseTractFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DateInput
            label={descriptor.label}
            valueFormat="YYYY-MM-DD"
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            clearable
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

/**
 * NumberInput field. Interest columns (royalty/orri_decimal/wi_decimal) are
 * NUMERIC(12,10) fractional-ownership decimals — `decimalScale: 10` so they
 * round-trip exactly; acreage columns (gross_acres/net_acres) are
 * NUMERIC(12,2) — `decimalScale: 2`.
 */
function numberField(
  label: string,
  defaultSpan: number,
  opts: { decimalScale?: number; step?: number } = {},
): LeaseTractFieldDescriptor {
  const decimalScale = opts.decimalScale ?? 10;
  const step = opts.step ?? (decimalScale > 0 ? 10 ** -decimalScale : 1);
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <NumberInput
            label={descriptor.label}
            decimalScale={decimalScale}
            step={step}
            min={0}
            value={(field.value as number | null) ?? ''}
            onChange={(v) => field.onChange(typeof v === 'number' ? v : null)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

/**
 * Reviewer dropdown: a Select of the current project's members. Behavior is
 * identical to well-fields.tsx / lease-fields.tsx's `reviewerField` (the
 * change-only authz lives server-side in api/src/routes/lease-tracts.ts's
 * assertReviewerAssignable) — mirrored, not reimplemented from scratch. For
 * moderator/editor callers (`context.canAssignAnyReviewer === false`) the
 * option list is restricted to self-only, but the CURRENT value must still
 * be visible and round-trip unchanged: if it belongs to someone else the
 * Select renders disabled (visible, not reassignable); if it's already self
 * (or empty) the normal self-only picker applies.
 */
const reviewerField: LeaseTractFieldDescriptor = {
  label: 'Reviewer',
  defaultSpan: 4,
  render: ({ name, control, disabled, context }) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const currentValue = (field.value as string | null) ?? null;
          const isOtherReviewer =
            !context.canAssignAnyReviewer &&
            currentValue !== null &&
            currentValue !== context.currentUserId;
          const options = context.canAssignAnyReviewer
            ? context.members
            : context.members.filter((m) => m.value === context.currentUserId);
          if (isOtherReviewer && !options.some((m) => m.value === currentValue)) {
            const known = context.members.find((m) => m.value === currentValue);
            options.push({ value: currentValue, label: known?.label ?? currentValue });
          }
          return (
            <Select
              // Force a remount once the project-members fetch resolves and
              // actually changes the resolvable option set for the current
              // value — Mantine's Select only re-derives its displayed
              // label from `data` via an internal effect keyed on
              // (value, selectedOption), which doesn't reliably re-sync the
              // rendered text once the control has already committed with
              // `data` still empty (e.g. the members query settling in a
              // later render than the reviewer field's first paint). A
              // fresh mount always computes the label from the CURRENT
              // (already-correct) data/value pair.
              key={`${currentValue ?? 'none'}-${context.members.length}`}
              label="Reviewer"
              data={options}
              searchable
              clearable
              value={currentValue}
              onChange={(v) => field.onChange(v ?? null)}
              disabled={disabled || isOtherReviewer}
            />
          );
        }}
      />
    );
  },
};

export const LEASE_TRACT_FORM_FIELDS: FieldRegistry<
  LeaseTractInputSchema,
  LeaseTractFormFieldName,
  LeaseTractFormContext
> = {
  agreement_number: textField('Agreement #', 4, { required: true }),
  type: textField('Type', 4),
  status: textField('Status', 4),
  reviewer_user_id: reviewerField,

  lessor: textField('Lessor', 6),
  lessee: textField('Lessee', 6),

  agreement_date: dateField('Agreement date', 4),
  expiration_date: dateField('Expiration date', 4),

  book: textField('Book', 3),
  page: textField('Page', 3),
  county: textField('County', 3),
  state: textField('State', 3),

  legal_description: textareaField('Legal description', 12),
  legal_remark: textareaField('Legal remark', 8),
  all_depths: textField('All depths', 4),

  gross_acres: numberField('Gross acres', 6, { decimalScale: 2 }),
  net_acres: numberField('Net acres', 6, { decimalScale: 2 }),

  royalty: numberField('Royalty', 4, { decimalScale: 10 }),
  orri_owner: textField('ORRI owner', 4),
  orri_decimal: numberField('ORRI decimal', 4, { decimalScale: 10 }),
  wi_owner: textField('WI owner', 4),
  wi_decimal: numberField('WI decimal', 4, { decimalScale: 10 }),
  other_burdens: textField('Other burdens', 4),
};

/**
 * Built-in default. Groups mirror
 * api/src/lib/lease-tract-form-layout-schema.ts's `DEFAULT_LAYOUT` (identity
 * / parties / dates / recording / legal / acreage / interest — spec §2.1)
 * so a freshly-deployed DB renders sensibly before any admin save.
 */
export const LEASE_TRACT_DEFAULT_LAYOUT: LayoutDocument<LeaseTractFormFieldName> = {
  version: 1,
  groups: [
    {
      id: 'identity',
      title: 'Identity',
      fields: [
        { name: 'agreement_number', span: 4 },
        { name: 'type', span: 4 },
        { name: 'status', span: 4 },
        { name: 'reviewer_user_id', span: 4 },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      fields: [
        { name: 'lessor', span: 6 },
        { name: 'lessee', span: 6 },
      ],
    },
    {
      id: 'dates',
      title: 'Dates',
      fields: [
        { name: 'agreement_date', span: 4 },
        { name: 'expiration_date', span: 4 },
      ],
    },
    {
      id: 'recording',
      title: 'Recording',
      fields: [
        { name: 'book', span: 3 },
        { name: 'page', span: 3 },
        { name: 'county', span: 3 },
        { name: 'state', span: 3 },
      ],
    },
    {
      id: 'legal',
      title: 'Legal',
      fields: [
        { name: 'legal_description', span: 12 },
        { name: 'legal_remark', span: 8 },
        { name: 'all_depths', span: 4 },
      ],
    },
    {
      id: 'acreage',
      title: 'Acreage',
      fields: [
        { name: 'gross_acres', span: 6 },
        { name: 'net_acres', span: 6 },
      ],
    },
    {
      id: 'interest',
      title: 'Interest',
      fields: [
        { name: 'royalty', span: 4 },
        { name: 'orri_owner', span: 4 },
        { name: 'orri_decimal', span: 4 },
        { name: 'wi_owner', span: 4 },
        { name: 'wi_decimal', span: 4 },
        { name: 'other_burdens', span: 4 },
      ],
    },
  ],
};
